import type React from 'react';
import { useEffect } from 'react';

import { Text } from '@change/ds-core-ui';
import { BaseSlider } from '@change/ds-forms';

import { useI18n } from '@change-corgi/core-react/i18n';

import { usePsfSharePageData } from '#src/app/pages/psf/share/pageContext';
import { useCurrentUserId } from '#src/app/shared/hooks/session';

import { ShareFeed } from '../ShareFeed';

import { GoalShareActions } from './components/GoalShareActions';
import { formatGoal, MAX_GOAL, MIN_GOAL } from './constants';
import { useReachGoalStep } from './hooks/useReachGoalStep';
import { useSetGoalStep } from './hooks/useSetGoalStep';

export const SharingGoalFlow: React.FC = () => {
	const { translate, translatePlural } = useI18n();
	const { data } = usePsfSharePageData();
	const petitionId = data.petition.id;
	const userId = useCurrentUserId();
	const {
		data: { userName, shareFeed },
	} = useReachGoalStep();
	const { goalValue, setGoalValue, trackSliderChange, saveGoal, emoji, message } = useSetGoalStep({
		petitionId,
		userId,
		initialShareGoal: data.initialShareGoal,
	});

	useEffect(() => {
		window.scrollTo({ top: 0, behavior: 'smooth' });
	}, []);

	return (
		<>
			<div className="flex flex-col gap-8 px-6 md:gap-9 md:px-10">
				<div className="flex flex-col gap-3 text-center">
					<div className="flex flex-col gap-2">
						<Text as="p" fontWeight="bold" className="text-typography-weak m-0">
							{translate('corgi.psf.share.sharing_goal.greeting', { user_name: userName })}
						</Text>
						<Text variant="heading" size="large" as="h1" className="text-typography-strong m-0">
							{translate('corgi.psf.share.sharing_goal.heading')}
						</Text>
					</div>
					<Text variant="body" className="text-typography-strong">
						{translate('corgi.psf.share.sharing_goal.body')}
					</Text>
				</div>

				<div className="mt-2 flex flex-col gap-2 px-4">
					<BaseSlider
						ariaLabel={translate('corgi.psf.share.sharing_goal.slider_aria_label')}
						value={goalValue}
						minValue={MIN_GOAL}
						maxValue={MAX_GOAL}
						step={1}
						onChange={setGoalValue}
						onChangeEnd={(value: number) => {
							trackSliderChange(value);
						}}
						showTooltip
						size="thick"
						tooltipTextSize="medium"
						formatValue={(value: number) =>
							translatePlural('corgi.psf.share.sharing_goal.slider_label', value, { count: formatGoal(value) })
						}
						data-qa="sharing-goal-slider"
						tooltipAlignment="follow"
					/>

					<Text variant="body" size="small" className="text-typography-weak">
						<span aria-hidden>{emoji}</span> {message}
					</Text>
				</div>

				<GoalShareActions onShareSuccess={saveGoal} />
			</div>

			{shareFeed.showFeed && (
				<div className="relative mt-7 h-full overflow-hidden md:mt-9" data-qa="share-feed">
					<ShareFeed items={shareFeed.items} />
				</div>
			)}
		</>
	);
};
